import { Bullet, color, Label, p50 } from '@amcharts/amcharts5';
import { Constant, SeriesType } from '@/modules/core/legacy/charts/am5/charts.constants';
import { ColumnFormat } from '@/modules/core/app/constants/data.constants';
import { useBullets as useGenericBullets } from '@/modules/core/legacy/charts/am5/base/composables/useBullets';
import { useNumberFormatter } from '@/modules/core/legacy/charts/am5/gauge/composables/useNumberFormatter';
import { XYSeries } from '@amcharts/amcharts5/xy';
import { useToolTips } from '@/modules/core/legacy/charts/am5/pie/composables/useTooltips';
import { useXYSeries } from '@/modules/core/legacy/charts/am5/base/composables/series/useXYSeries';
import { GoalProgressBarOptions } from '@/modules/ta/goal/goal.constants';
import { PlotType } from '@/modules/ta/legacy/widget/widget.constants';
import { useBullets } from '@/modules/core/legacy/charts/am5/line/composables/useBullets';
import { isNull } from 'lodash';
import { getChartFontColor } from '@/modules/core/charts/am5/charts.helper';

export function useMetricLabels(context) {
  const { root, chart, config, isDarkTheme } = context();

  const { hidePlotContainerOutsideBullets } = useGenericBullets(context);
  const { formatNumber } = useNumberFormatter(context);
  const { assignThemeColor } = useToolTips(context);
  const { isDataWithDate } = useXYSeries(context);

  function createMetricLabels(createBenchmarkLabel) {
    const { isRotated, fontColorPicker } = config.value;

    const { sliceChartTemplate } = useBullets(context);

    chart.value.series.each((series) => {
      const userData = series.get(Constant.USER_DATA);
      const { showMetricLabels, dataItemFormat, lineType, seriesType } = userData;

      // ensure we're only adding the metric value bullet once - if it's coming in from
      // the benchmark it won't be a full XYSeries
      if (userData.constructor.name === XYSeries.name && createBenchmarkLabel) {
        return;
      }

      if (showMetricLabels && seriesType !== SeriesType.COLUMN) {
        const suffix = dataItemFormat === ColumnFormat.FORMAT_PERCENT ? '%' : '';
        const baseColor =
          lineType === Constant.BENCHMARK
            ? userData.labelFill
            : assignThemeColor(isDarkTheme, config.value);

        const fill = isNull(fontColorPicker) ? series.get('fill') ?? baseColor : color(baseColor);

        series.bullets.push(() => {
          const label = Label.new(
            root.value,
            {
              centerY: 26,
              centerX: p50,
              // eslint-disable-next-line tap/no-raw-text-js
              text: `{${isRotated ? 'valueX' : 'valueY'}.formatNumber()}${suffix}`,
              fill,
              fontSize: 12,
              populateText: true,
            },
            sliceChartTemplate
          );

          hidePlotContainerOutsideBullets(label);

          const labelBullet = Bullet.new(root.value, {
            sprite: label,
          });

          labelBullet.get(Constant.SPRITE).text.adapters.add(Constant.TEXT, (text, target) => {
            const { dataItem } = target;
            const field = dataItem.component.get(
              isRotated ? Constant.VALUE_X_FIELD : Constant.VALUE_Y_FIELD
            );
            const value = dataItem.dataContext[field];
            const seriesUserData = dataItem.component.get(Constant.USER_DATA);

            return formatNumber(value, seriesUserData.dataItemFormat);
          });

          return labelBullet;
        });
      }
    });
  }

  function handleOverlapForInitialToFinal() {
    if (isDataWithDate()) {
      const { lineChartTemplate } = useBullets(context);
      // Remove old event handlers first
      lineChartTemplate.events.off(GoalProgressBarOptions.POSITION_CHANGED);

      lineChartTemplate.events.on(GoalProgressBarOptions.POSITION_CHANGED, () => {
        processCollidedLabels();
      });
    }
  }

  function processCollidedLabels() {
    const { series } = chart.value;
    const { plotType } = config.value;
    const spritesArray = [];
    series.each((itemSeries, index) => {
      const lastDataItem = itemSeries.dataItems[itemSeries.dataItems.length - 1];
      if (lastDataItem?.bullets) {
        const correctBullet = lastDataItem.bullets[2]
          ? lastDataItem.bullets[2]
          : lastDataItem.bullets[1]; // adjust bullet index to get label accordingly
        if (correctBullet) {
          if (!correctBullet) return;
          const sprite = correctBullet?.get && correctBullet.get(Constant.SPRITE);
          if (sprite) {
            spritesArray.push({
              sprite,
              x: sprite.x(),
              y: sprite.y(),
            });
          }
        }
      }
    });

    if (!spritesArray.length) return;
    // sorting the number to plot in descending
    spritesArray.sort((a, b) => a.y - b.y);

    const threshold = 20; // pixels, adjust based on your label width
    const clusters = [];
    let currentCluster = [spritesArray[0]];
    // find x coordinates which are close to each other.
    for (let i = 1; i < spritesArray.length; i++) {
      const prev = spritesArray[i - 1];
      const curr = spritesArray[i];

      if (curr.y - prev.y <= threshold) {
        // close enough → same cluster
        currentCluster.push(curr);
      } else {
        clusters.push(currentCluster);
        currentCluster = [curr];
      }
    }
    clusters.push(currentCluster); // last cluster

    // Spread labels horizontally within clusters
    clusters.forEach((cluster) => {
      if (cluster.length === 1) return; // no need to adjust

      const n = cluster.length;
      const spacing = plotType === PlotType.FULL_STACKED_V2 ? 70 : 40; // horizontal spacing between labels
      const startX = cluster[0].x - ((n - 1) / 2) * spacing; // center around the first bullet

      cluster.forEach((item, index) => {
        const targetX = startX + index * spacing;
        const dx = targetX - item.x; // offset relative to current x
        item.sprite.set('dx', dx);
      });
    });
  }

  return {
    createMetricLabels,
    handleOverlapForInitialToFinal,
  };
}
